/**
 * The people list for one day: everyone on the week's roster with what they
 * are doing that day — free, on which lines for how many hours, or out —
 * so a planner can see who is still available before dragging a name.
 */
import { AvailabilityRow, RosterAssignment } from "../../../../../services/roster-assignments-api";
import { localDay } from "../../../utils/time";
import { KnownWorker } from "../ui/standing-crew-panel";
import { isConfirmedRow } from "./availability";

export type PersonDayState = "free" | "working" | "out";

export interface PersonDay {
	worker: KnownWorker;
	state: PersonDayState;
	/** Lines the person is on that day, in order of first block. */
	lines: string[];
	/** Hours on the roster that day. */
	hours: number;
	/** The row that marks them out (or requesting) that day, if any. */
	marker: AvailabilityRow | null;
}

const STATE_RANK: Record<PersonDayState, number> = { free: 0, working: 1, out: 2 };

export function dayPeople(workers: KnownWorker[], assignments: RosterAssignment[], dayIso: string): PersonDay[] {
	const byWorker = new Map<string, RosterAssignment[]>();
	assignments.forEach((a) => {
		if (localDay(new Date(a.startTime)) !== dayIso) return;
		const key = String(a.workerId);
		byWorker.set(key, [...(byWorker.get(key) ?? []), a]);
	});
	return workers
		.map<PersonDay>((worker) => {
			const blocks = (byWorker.get(String(worker.id)) ?? []).sort((a, b) => a.startTime.localeCompare(b.startTime));
			const lines: string[] = [];
			blocks.forEach((b) => {
				if (b.workstation && !lines.includes(b.workstation)) lines.push(b.workstation);
			});
			const hours = blocks.reduce((total, b) => total + (new Date(b.endTime).getTime() - new Date(b.startTime).getTime()) / 3600000, 0);
			const rows = (worker.unavailable ?? []).filter((row) => row.date === dayIso);
			const marker = rows.find(isConfirmedRow) ?? rows[0] ?? null;
			const state: PersonDayState = marker && isConfirmedRow(marker) ? "out" : blocks.length ? "working" : "free";
			return { worker, state, lines, hours: Math.round(hours * 4) / 4, marker };
		})
		.sort((a, b) => STATE_RANK[a.state] - STATE_RANK[b.state] || a.worker.name.localeCompare(b.worker.name));
}

/** "Free", "Bead Tower · 8.5 h", "Out" — the one line under a name. */
export function personDayLabel(person: PersonDay): string {
	if (person.state === "out") return person.marker?.label || "Out";
	if (person.state === "free") return "Free";
	const hours = person.hours % 1 === 0 ? String(person.hours) : person.hours.toFixed(2).replace(/0$/, "");
	return `${person.lines.join(", ")} · ${hours} h`;
}
